import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";

const cyan = "#00f5ff";
const border = "rgba(0,245,255,0.15)";

const inputStyle = {
  width: "100%",
  backgroundColor: "rgba(0,245,255,0.05)",
  border: `1px solid ${border}`,
  borderRadius: "6px",
  padding: "10px 14px",
  color: "white",
  fontSize: "13px",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "'Segoe UI', sans-serif",
};
const labelStyle = {
  color: cyan,
  fontSize: "10px",
  letterSpacing: "2px",
  display: "block",
  marginBottom: "6px",
  fontWeight: "700",
};

// Same modal pattern as FeedbackButton.jsx, mounted alongside it in
// Dashboard.js's top nav. Deliberately keeps its own local `phone` state
// seeded from the `user` prop rather than lifting a change back up into
// App.js's user state — nothing else in the app reads user.phone
// reactively today, so there's no need to thread a new update callback
// through AppContent/Dashboard just for this.
// buttonStyle: overrides for the trigger button (Dashboard passes the
// phone-header size — at least 44px tall — on mobile).
export default function SmsSettingsButton({ token, user, buttonStyle }) {
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(user?.phone || "");
  // Re-asked on every open rather than persisted — the checkbox must be
  // checked before *this* submission, every time a phone number is being
  // saved, not just the first time the user ever used this form.
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  // Opting OUT (clearing the number) never needs consent — only
  // submitting a non-blank number does, since that's the action that
  // actually enrolls the user in SMS.
  const needsConsent = phone.trim() !== "";
  const canSave = !needsConsent || consent;

  function openModal() {
    setPhone(user?.phone || "");
    setConsent(false);
    setError("");
    setSaved(false);
    setOpen(true);
  }

  async function handleSave() {
    // Defense in depth alongside the disabled Save button below — this
    // is the actual client-side block the compliance requirement cares
    // about, not just a visual affordance.
    if (needsConsent && !consent) {
      setError("Check the consent box above before saving a phone number.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/users/me/phone`,
        { phone },
        { headers: { Authorization: "Bearer " + token } },
      );
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save — try again");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button
        onClick={openModal}
        style={{
          color: "rgba(0,245,255,0.7)",
          background: "none",
          border: `1px solid ${border}`,
          cursor: "pointer",
          fontSize: "11px",
          padding: "4px 12px",
          borderRadius: "4px",
          letterSpacing: "1px",
          whiteSpace: "nowrap",
          ...buttonStyle,
        }}
      >
        {tr("header.smsAlerts")}
      </button>

      {/* Portaled to document.body rather than rendered inline here —
          this button lives inside Dashboard.js's <nav>, which sets
          backdropFilter for its glass effect. backdrop-filter (like
          filter/transform) creates a new containing block for any
          position:fixed descendant, so without the portal this overlay's
          inset:0 resolves against the nav's small box instead of the
          real viewport, clipping the modal near the top of the screen
          instead of centering it. */}
      {open && createPortal(
        <div
          style={{
            backgroundColor: "rgba(0,0,0,0.85)",
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "#020817",
              border: `1px solid ${border}`,
              borderRadius: "12px",
              padding: "32px",
              width: "100%",
              maxWidth: "420px",
              boxShadow: "0 0 60px rgba(0,245,255,0.1)",
            }}
          >
            {saved ? (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <div style={{ fontSize: "40px", marginBottom: "12px" }}>✓</div>
                <div
                  style={{
                    color: "#00cc66",
                    fontSize: "14px",
                    fontWeight: "700",
                    marginBottom: "8px",
                  }}
                >
                  {phone.trim() ? "SMS alerts number saved." : "SMS alerts turned off."}
                </div>
                <button
                  onClick={() => setOpen(false)}
                  style={{
                    background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                    color: "#020817",
                    fontWeight: "700",
                    fontSize: "11px",
                    letterSpacing: "2px",
                    padding: "10px 24px",
                    borderRadius: "6px",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  CLOSE
                </button>
              </div>
            ) : (
              <>
                <div
                  style={{
                    color: cyan,
                    fontSize: "12px",
                    fontWeight: "700",
                    letterSpacing: "3px",
                    marginBottom: "12px",
                  }}
                >
                  SMS SAFETY ALERTS
                </div>
                <p
                  style={{
                    color: "rgba(255,255,255,0.5)",
                    fontSize: "12px",
                    lineHeight: "1.6",
                    marginBottom: "20px",
                  }}
                >
                  Admins and supervisors with a phone number on file get a
                  text the moment a Safety Incident or Near-Miss is filed.
                  Leave this blank to opt out.
                </p>

                <label style={labelStyle}>PHONE NUMBER</label>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(601) 996-5151"
                  style={inputStyle}
                />

                {needsConsent && (
                  <label
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px",
                      marginTop: "16px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      style={{ marginTop: "2px", flexShrink: 0 }}
                    />
                    <span
                      style={{
                        color: "rgba(255,255,255,0.7)",
                        fontSize: "12px",
                        lineHeight: "1.6",
                      }}
                    >
                      I agree to receive SMS safety alerts from RigIQ.
                      Message and data rates may apply. Reply STOP to opt
                      out, HELP for help.
                    </span>
                  </label>
                )}

                {error && (
                  <div style={{ color: "#ff4466", fontSize: "12px", marginTop: "12px" }}>
                    ⚠ {error}
                  </div>
                )}

                <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
                  <button
                    onClick={() => setOpen(false)}
                    style={{
                      flex: 1,
                      background: "none",
                      border: `1px solid ${border}`,
                      color: "rgba(0,245,255,0.5)",
                      padding: "10px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "11px",
                      letterSpacing: "2px",
                    }}
                  >
                    CANCEL
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !canSave}
                    style={{
                      flex: 2,
                      background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                      color: "#020817",
                      fontWeight: "700",
                      fontSize: "11px",
                      letterSpacing: "2px",
                      padding: "10px",
                      borderRadius: "6px",
                      border: "none",
                      cursor: saving || !canSave ? "not-allowed" : "pointer",
                      opacity: saving || !canSave ? 0.5 : 1,
                    }}
                  >
                    {saving ? "SAVING..." : "SAVE"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
